import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Store, Truck, Bell, Save, Loader2, Mail, Send } from 'lucide-react';

function AdminSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const token = localStorage.getItem('adminToken');

  // Store Settings
  const [storeSettings, setStoreSettings] = useState({
    store_name: 'Tiffin & Bag',
    tagline: 'Packed with Fun!',
    email: '',
    phone: '',
    address: '',
    currency: 'INR',
  });

  // Shipping Settings
  const [shippingSettings, setShippingSettings] = useState({
    free_delivery: true,
    delivery_charge: 0,
    min_order_for_free: 0,
    same_day_dispatch_time: '14:00',
    delivery_time_metro: '2-3 business days',
    delivery_time_other: '4-5 business days',
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    order_confirmation: true,
    shipping_update: true,
    delivery_confirmation: true,
    marketing_emails: false,
    admin_email: '',
    admin_new_order_alert: true,
  });

  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchAllSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const [storeRes, shippingRes, notificationRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/store`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/shipping`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (storeRes.ok) {
        const data = await storeRes.json();
        setStoreSettings(prev => ({ ...prev, ...data.settings }));
      }
      if (shippingRes.ok) {
        const data = await shippingRes.json();
        setShippingSettings(prev => ({ ...prev, ...data.settings }));
      }
      if (notificationRes.ok) {
        const data = await notificationRes.json();
        setNotificationSettings(prev => ({ ...prev, ...data.settings }));
        setTestEmail(data.settings.admin_email || '');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveStoreSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/store`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(storeSettings),
      });
      if (response.ok) {
        toast.success('Store settings saved!');
      } else {
        toast.error('Failed to save store settings');
      }
    } catch (error) {
      toast.error('Failed to save store settings');
    } finally {
      setSaving(false);
    }
  };

  const saveShippingSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/shipping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(shippingSettings),
      });
      if (response.ok) {
        toast.success('Shipping settings saved!');
      } else {
        toast.error('Failed to save shipping settings');
      }
    } catch (error) {
      toast.error('Failed to save shipping settings');
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(notificationSettings),
      });
      if (response.ok) {
        toast.success('Notification settings saved!');
      } else {
        toast.error('Failed to save notification settings');
      }
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setTestingEmail(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email: testEmail }),
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success(`Test email sent to ${testEmail}!`);
      } else {
        toast.error(result.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        <Tabs defaultValue="store" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="store" className="flex items-center gap-2">
              <Store className="w-4 h-4" />
              <span className="hidden sm:inline">Store</span>
            </TabsTrigger>
            <TabsTrigger value="shipping" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              <span className="hidden sm:inline">Shipping</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Notifications</span>
            </TabsTrigger>
          </TabsList>

          {/* Store Settings */}
          <TabsContent value="store">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Store className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold">Store Information</h2>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label htmlFor="store_name">Store Name</Label>
                  <Input
                    id="store_name"
                    value={storeSettings.store_name}
                    onChange={(e) => setStoreSettings({ ...storeSettings, store_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    value={storeSettings.tagline}
                    onChange={(e) => setStoreSettings({ ...storeSettings, tagline: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="store_email">Contact Email</Label>
                  <Input
                    id="store_email"
                    type="email"
                    value={storeSettings.email}
                    onChange={(e) => setStoreSettings({ ...storeSettings, email: e.target.value })}
                    placeholder="support@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="store_phone">Contact Phone</Label>
                  <Input
                    id="store_phone"
                    value={storeSettings.phone}
                    onChange={(e) => setStoreSettings({ ...storeSettings, phone: e.target.value })}
                    placeholder="+91 80621 80632"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="store_address">Store Address</Label>
                  <Input
                    id="store_address"
                    value={storeSettings.address}
                    onChange={(e) => setStoreSettings({ ...storeSettings, address: e.target.value })}
                    placeholder="Mumbai, India"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={saveStoreSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Store Settings
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Shipping Settings */}
          <TabsContent value="shipping">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Truck className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold">Shipping & Delivery</h2>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <Label className="text-base">Free Delivery</Label>
                    <p className="text-sm text-muted-foreground">Enable free delivery for all orders</p>
                  </div>
                  <Switch
                    checked={shippingSettings.free_delivery}
                    onCheckedChange={(checked) => setShippingSettings({ ...shippingSettings, free_delivery: checked })}
                  />
                </div>

                {!shippingSettings.free_delivery && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="delivery_charge">Delivery Charge (Rs)</Label>
                      <Input
                        id="delivery_charge"
                        type="number"
                        value={shippingSettings.delivery_charge}
                        onChange={(e) => setShippingSettings({ ...shippingSettings, delivery_charge: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="min_order_for_free">Minimum Order for Free Delivery (Rs)</Label>
                      <Input
                        id="min_order_for_free"
                        type="number"
                        value={shippingSettings.min_order_for_free}
                        onChange={(e) => setShippingSettings({ ...shippingSettings, min_order_for_free: Number(e.target.value) })}
                      />
                    </div>
                  </div>
                )}

                <Separator />

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="delivery_time_metro">Metro Cities Delivery Time</Label>
                    <Input
                      id="delivery_time_metro"
                      value={shippingSettings.delivery_time_metro}
                      onChange={(e) => setShippingSettings({ ...shippingSettings, delivery_time_metro: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="delivery_time_other">Other Cities Delivery Time</Label>
                    <Input
                      id="delivery_time_other"
                      value={shippingSettings.delivery_time_other}
                      onChange={(e) => setShippingSettings({ ...shippingSettings, delivery_time_other: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="same_day_dispatch_time">Same Day Dispatch Cutoff Time</Label>
                  <Input
                    id="same_day_dispatch_time"
                    type="time"
                    value={shippingSettings.same_day_dispatch_time}
                    onChange={(e) => setShippingSettings({ ...shippingSettings, same_day_dispatch_time: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Orders placed before this time will be dispatched same day</p>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={saveShippingSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Shipping Settings
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Notification Settings */}
          <TabsContent value="notifications">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Bell className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold">Email Notifications</h2>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-medium">Customer Notifications</h3>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-sm">Order Confirmation</Label>
                      <p className="text-xs text-muted-foreground">Send email when order is placed</p>
                    </div>
                    <Switch
                      checked={notificationSettings.order_confirmation}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, order_confirmation: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-sm">Shipping Update</Label>
                      <p className="text-xs text-muted-foreground">Send email when order is shipped</p>
                    </div>
                    <Switch
                      checked={notificationSettings.shipping_update}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, shipping_update: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-sm">Delivery Confirmation</Label>
                      <p className="text-xs text-muted-foreground">Send email when order is delivered</p>
                    </div>
                    <Switch
                      checked={notificationSettings.delivery_confirmation}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, delivery_confirmation: checked })}
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="font-medium">Admin Notifications</h3>
                  
                  <div>
                    <Label htmlFor="admin_email">Admin Email Address</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={notificationSettings.admin_email}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, admin_email: e.target.value })}
                      placeholder="admin@tiffinandbag.com"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Receive order notifications at this email</p>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-sm">New Order Alert</Label>
                      <p className="text-xs text-muted-foreground">Receive email for every new order</p>
                    </div>
                    <Switch
                      checked={notificationSettings.admin_new_order_alert}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, admin_new_order_alert: checked })}
                    />
                  </div>
                </div>

                <Separator />

                {/* Test Email Section */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <h3 className="font-medium text-blue-800">Test Email Configuration</h3>
                  </div>
                  <p className="text-sm text-blue-700 mb-3">
                    Send a test email to verify your email configuration is working.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1"
                    />
                    <Button onClick={sendTestEmail} disabled={testingEmail} variant="outline">
                      {testingEmail ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Test
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={saveNotificationSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Notification Settings
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

export default AdminSettingsPage;
